import React, { useState, useEffect } from 'react';
import { accountsApi } from '../../services/jiyuMailboxApi';
import { Layout, Card, Row, Col, Button, Typography, message, Space, Alert } from 'antd';
import { 
  HeartOutlined, 
  RobotOutlined, 
  ClockCircleOutlined, 
  HistoryOutlined,
  CheckCircleOutlined,
  SettingOutlined,
  MessageOutlined,
  UserOutlined,
  CalendarOutlined,
  FormOutlined
} from '@ant-design/icons';
import WechatConfigPanel from './WechatConfig/WechatConfigPanel';
import ReminderConfigPanel from './ReminderConfig/ReminderConfigPanel';
import ReminderHistoryPanel from './ReminderConfig/ReminderHistoryPanel';

const { Title, Paragraph, Text } = Typography;
const { Header, Content, Footer } = Layout;

// 本地存储键名
const STORAGE_KEYS = {
  WECHAT_CONFIGURED: 'jiyu_wechat_configured',
  CONFIG_ID: 'jiyu_config_id',
  REMINDERS: 'jiyu_reminders',
  CURRENT_REMINDER: 'jiyu_current_reminder'
};

// 提醒配置接口
interface ReminderConfig {
  id: string;
  recipientName: string;
  reminderDate: string;
  reminderTime: string;
  reminderContent: string;
  aiPreview: string;
  status: 'draft' | 'scheduled' | 'sent' | 'failed';
  createdAt: string;
  scheduledAt?: string;
  sentAt?: string;
}

const JiyuMailboxPage: React.FC = () => {
  // 状态管理
  const [wechatConfigured, setWechatConfigured] = useState<boolean>(false);
  const [configId, setConfigId] = useState<string>('');
  const [reminders, setReminders] = useState<ReminderConfig[]>([]);
  const [currentReminder, setCurrentReminder] = useState<Partial<ReminderConfig>>({});
  const [activeTab, setActiveTab] = useState<string>('overview');
  const [openclawStatus, setOpenclawStatus] = useState<any>(null);
  const [wechatAccounts, setWechatAccounts] = useState<any[]>([]);

  // 初始化加载本地存储数据
  useEffect(() => {
    loadFromLocalStorage();
    checkOpenClawStatus();
  }, []);

  // 检查OpenClaw状态
  const checkOpenClawStatus = async () => {
    try {
      const status = await accountsApi.getAll();
      setWechatAccounts(status);
      console.log('已连接的微信账号:', status);
    } catch (error) {
      console.error('检查微信账号失败:', error);
    }
  };
  
  // 加载本地存储数据
  const loadFromLocalStorage = () => {
    try {
      // 加载微信配置状态
      const configured = localStorage.getItem(STORAGE_KEYS.WECHAT_CONFIGURED);
      setWechatConfigured(configured === 'true');
      
      // 加载配置ID
      const savedConfigId = localStorage.getItem(STORAGE_KEYS.CONFIG_ID);
      if (savedConfigId) setConfigId(savedConfigId);
      
      // 加载提醒记录
      const remindersJson = localStorage.getItem(STORAGE_KEYS.REMINDERS);
      if (remindersJson) {
        setReminders(JSON.parse(remindersJson));
      }
      
      // 加载当前提醒草稿
      const currentReminderJson = localStorage.getItem(STORAGE_KEYS.CURRENT_REMINDER);
      if (currentReminderJson) {
        setCurrentReminder(JSON.parse(currentReminderJson));
      }
      
      console.log('📦 本地存储数据加载完成');
    } catch (error) {
      console.error('❌ 加载本地存储数据失败:', error);
      message.error('加载数据失败，请刷新页面重试');
    }
  };
  
  // 保存到本地存储
  const saveToLocalStorage = (key: string, data: any) => {
    try {
      if (typeof data === 'object') {
        localStorage.setItem(key, JSON.stringify(data));
      } else {
        localStorage.setItem(key, data.toString());
      }
    } catch (error) {
      console.error('❌ 保存到本地存储失败:', error);
    }
  };
  
  // 微信配置成功回调
  const handleWechatConfigSuccess = (newConfigId: string) => {
    setWechatConfigured(true);
    setConfigId(newConfigId);
    
    // 保存到本地存储
    saveToLocalStorage(STORAGE_KEYS.WECHAT_CONFIGURED, true);
    saveToLocalStorage(STORAGE_KEYS.CONFIG_ID, newConfigId);
    
    message.success('🎉 微信机器人配置成功！');
    setActiveTab('reminder');
  };
  
  // 添加新提醒
  const handleAddReminder = (reminder: ReminderConfig) => {
    const updatedReminders = [reminder, ...reminders];
    setReminders(updatedReminders);

    // 保存到本地存储
    saveToLocalStorage(STORAGE_KEYS.REMINDERS, updatedReminders);

    message.success('💌 暖心提醒设置成功！');
    setActiveTab('history');
  };

  // 删除提醒
  const handleDeleteReminder = (id: string) => {
    const updatedReminders = reminders.filter(r => r.id !== id);
    setReminders(updatedReminders);

    // 保存到本地存储
    saveToLocalStorage(STORAGE_KEYS.REMINDERS, updatedReminders);

    message.success('🗑️ 提醒已删除');
  };
  
  // 更新当前提醒草稿
  const handleUpdateCurrentReminder = (reminder: Partial<ReminderConfig>) => {
    const updatedReminder = { ...currentReminder, ...reminder };
    setCurrentReminder(updatedReminder);
    
    // 保存到本地存储
    saveToLocalStorage(STORAGE_KEYS.CURRENT_REMINDER, updatedReminder);
  };
  
  // 清除所有数据
  const handleClearAllData = () => {
    if (window.confirm('确定要清除所有数据吗？这将删除所有配置和提醒记录。')) {
      localStorage.removeItem(STORAGE_KEYS.WECHAT_CONFIGURED);
      localStorage.removeItem(STORAGE_KEYS.CONFIG_ID);
      localStorage.removeItem(STORAGE_KEYS.REMINDERS);
      localStorage.removeItem(STORAGE_KEYS.CURRENT_REMINDER);
      
      setWechatConfigured(false);
      setConfigId('');
      setReminders([]);
      setCurrentReminder({});
      
      message.success('🧹 所有数据已清除');
    }
  };
  
  return (
    <Layout style={{ 
      minHeight: '100vh', 
      background: 'linear-gradient(135deg, #fff9f9 0%, #f0f9ff 100%)',
      fontFamily: "'PingFang SC', 'Microsoft YaHei', sans-serif"
    }}>
      {/* 头部 - 温馨设计 */}
      <Header style={{
        background: 'linear-gradient(135deg, #ff6b6b, #ff8e8e)',
        color: 'white',
        padding: '0 20px',
        boxShadow: '0 2px 8px rgba(255, 107, 107, 0.2)',
        position: 'sticky',
        top: 0,
        zIndex: 100,
        height: 'auto',
        lineHeight: 'normal'
      }}>
        <Row align="middle" gutter={[16, 8]}>
          <Col xs={24} md={12}>
            <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap' }}>
              <Space size="small">
                <span style={{ fontSize: '24px', animation: 'float 3s ease-in-out infinite' }}>💌</span>
                <Title level={3} style={{ color: 'white', margin: 0, fontSize: '20px' }}>
                  寄语信箱
                </Title>
              </Space>
              <div style={{ display: 'flex', alignItems: 'center', marginLeft: '16px', flexWrap: 'wrap', gap: '8px' }}>
                <Button
                  type="text"
                  icon={<SettingOutlined />}
                  style={{ color: 'white', padding: '4px 8px' }}
                  onClick={() => setActiveTab('config')}
                >
                  微信配置
                </Button>
                <Button
                  type="text"
                  icon={<MessageOutlined />}
                  style={{ color: 'white', padding: '4px 8px' }}
                  onClick={() => setActiveTab('reminder')}
                >
                  提醒配置
                </Button>
                <Button
                  type="text"
                  icon={<HistoryOutlined />}
                  style={{ color: 'white', padding: '4px 8px' }}
                  onClick={() => setActiveTab('history')}
                >
                  历史记录
                </Button>
              </div>
            </div>
          </Col>
          <Col xs={24} md={12} style={{ textAlign: 'right', paddingLeft: '16px' }}>
            <Text style={{ color: 'rgba(255,255,255,0.9)', fontSize: '12px' }}>
              让心意准时到达，用AI温暖你的重要关系
            </Text>
          </Col>
        </Row>
      </Header>
      
      {/* 主要内容区域 */}
      <Content style={{ padding: '20px', maxWidth: '1200px', margin: '0 auto', width: '100%' }}>
        {/* 状态显示卡片） */}
        <Card
          style={{
            marginBottom: '20px',
            borderRadius: '12px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
            border: '1px solid #ffcccc'
          }}
        >
          <Row align="middle" gutter={[16, 16]}>
            <Col xs={24} sm={24} md={12}>
              <Space size="middle" style={{ width: '100%' }} wrap>
                <div style={{
                  width: '50px',
                  height: '50px',
                  borderRadius: '50%',
                  background: wechatConfigured
                    ? 'linear-gradient(135deg, #52c41a, #73d13d)'
                    : 'linear-gradient(135deg, #ff4d4f, #ff7875)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'white',
                  fontSize: '20px',
                  flexShrink: 0
                }}>
                  {wechatConfigured ? '✅' : '❌'}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <Title level={5} style={{ margin: 0, marginBottom: '4px' }}>
                    {wechatConfigured ? '微信机器人已配置' : '微信机器人未配置'}
                  </Title>
                  <Text type="secondary" style={{ fontSize: '12px' }}>
                    {wechatConfigured
                      ? `配置ID: ${configId?.slice(0, 8)}...`
                      : '请先配置微信机器人以发送提醒'
                    }
                  </Text>
                </div>
              </Space>
            </Col>
          </Row>

          {/* 显示已连接的微信账号 */}
          {wechatAccounts.length > 0 && (
            <div style={{ marginTop: '16px', padding: '12px', background: '#f0f9ff', borderRadius: '8px' }}>
              <Text strong style={{ color: '#52c41a', marginBottom: '8px', display: 'block' }}>
                📱 已连接的微信账号（{wechatAccounts.length}个）
              </Text>
              {wechatAccounts.map((account, index) => (
                <div
                  key={account.account_id}
                  style={{
                    padding: '8px',
                    background: 'white',
                    borderRadius: '6px',
                    marginBottom: index < wechatAccounts.length - 1 ? '8px' : 0,
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px'
                  }}
                >
                  <div
                    style={{
                      width: '40px',
                      height: '40px',
                      borderRadius: '50%',
                      background: account.status === 'connected' ? 'linear-gradient(135deg, #52c41a, #73d13d)' : 'linear-gradient(135deg, #ff4d4f, #ff7875)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: 'white',
                      fontSize: '16px',
                      flexShrink: 0
                    }}
                  >
                    {account.status === 'connected' ? '✓' : '✗'}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 'bold', fontSize: '14px', marginBottom: '2px' }}>
                      {account.user_id || '未知用户'}
                    </div>
                    <Text type="secondary" style={{ fontSize: '12px' }}>
                      账号ID: {account.account_id.slice(0, 12)}... |
                      状态: {account.status === 'connected' ? '已连接' : '未连接'}
                    </Text>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>

        {/* 根据活动标签显示不同内容 */}
        <div style={{ minHeight: '500px' }}>
          {activeTab === 'overview' && (
            <Card style={{ borderRadius: '12px', marginBottom: '20px' }}>
              <Title level={3} style={{ color: '#ff6b6b', marginBottom: '20px' }}>
                <HeartOutlined /> 寄语信箱 - 暖心的定时提醒机器人
              </Title>
              
              <Paragraph style={{ fontSize: '16px', lineHeight: 1.6, marginBottom: '30px' }}>
                这是一个充满温度的AI提醒工具，通过小龙虾AI模型，为你的重要关系发送暖心的定时提醒。
                让科技传递真情，让心意准时到达。
              </Paragraph>
              
              <Row gutter={[16, 16]}>
                <Col xs={24} sm={12} md={8}>
                  <Card
                    hoverable
                    style={{ textAlign: 'center', height: '100%', minHeight: '200px' }}
                    onClick={() => setActiveTab('config')}
                  >
                    <div style={{ fontSize: '36px', marginBottom: '12px', color: '#1890ff' }}>
                      <RobotOutlined />
                    </div>
                    <Title level={5}>微信机器人配置</Title>
                    <Paragraph type="secondary" style={{ fontSize: '12px', marginBottom: 0 }}>
                      连接你的微信，让小龙虾帮你发送消息
                    </Paragraph>
                  </Card>
                </Col>

                <Col xs={24} sm={12} md={8}>
                  <Card
                    hoverable={wechatConfigured}
                    style={{ textAlign: 'center', height: '100%', minHeight: '200px', opacity: wechatConfigured ? 1 : 0.5, cursor: wechatConfigured ? 'pointer' : 'not-allowed' }}
                    onClick={() => wechatConfigured && setActiveTab('reminder')}
                  >
                    <div style={{ fontSize: '36px', marginBottom: '12px', color: '#ff6b6b' }}>
                      <MessageOutlined />
                    </div>
                    <Title level={5}>提醒内容配置</Title>
                    <Paragraph type="secondary" style={{ fontSize: '12px', marginBottom: 0 }}>
                      AI分析需求，定时发送暖心提醒
                    </Paragraph>
                    {!wechatConfigured && (
                      <Alert message="需要先配置微信机器人" type="warning" showIcon style={{ marginTop: '10px', fontSize: '12px' }} />
                    )}
                  </Card>
                </Col>

                <Col xs={24} sm={12} md={8}>
                  <Card
                    hoverable
                    style={{ textAlign: 'center', height: '100%', minHeight: '200px' }}
                    onClick={() => setActiveTab('history')}
                  >
                    <div style={{ fontSize: '36px', marginBottom: '12px', color: '#52c41a' }}>
                      <HistoryOutlined />
                    </div>
                    <Title level={5}>历史记录查看</Title>
                    <Paragraph type="secondary" style={{ fontSize: '12px', marginBottom: 0 }}>
                      查看已配置的提醒和发送状态
                    </Paragraph>
                  </Card>
                </Col>
              </Row>
              
              <Alert
                message="测试版本提示"
                description="所有数据仅保存在您的浏览器本地，保护您的隐私。这是一个测试版本，仅供体验使用。"
                type="info"
                showIcon
                style={{ marginTop: '30px' }}
              />
            </Card>
          )}
          
          {activeTab === 'config' && (
            <WechatConfigPanel
              onConfigSuccess={handleWechatConfigSuccess}
              onSwitchToReminder={() => setActiveTab('reminder')}
              currentConfigId={configId}
              isConfigured={wechatConfigured}
            />
          )}
          
          {activeTab === 'reminder' && (
            <ReminderConfigPanel
              onAddReminder={handleAddReminder}
              currentReminder={currentReminder}
              onUpdateCurrentReminder={handleUpdateCurrentReminder}
              isWechatConfigured={wechatConfigured}
            />
          )}
          
          {activeTab === 'history' && (
            <ReminderHistoryPanel
              reminders={reminders}
              onClearAll={handleClearAllData}
              onDeleteReminder={handleDeleteReminder}
            />
          )}
        </div>
      </Content>
      
      {/* 底部 - 温馨设计 */}
      <Footer style={{
        background: 'linear-gradient(135deg, #2c3e50, #34495e)',
        color: 'white',
        padding: '20px',
        textAlign: 'center'
      }}>
        <div style={{ maxWidth: '800px', margin: '0 auto' }}>
          <div style={{ fontSize: '18px', marginBottom: '12px' }}>
            <HeartOutlined style={{ color: '#ff6b6b', marginRight: '8px' }} />
            寄语信箱 - 暖心的定时提醒机器人
          </div>

          <Paragraph style={{ opacity: 0.8, marginBottom: '16px', fontSize: '13px' }}>
            一个充满温度的AI提醒工具，让科技传递真情，让心意准时到达。
            每一次提醒，都是温暖的关怀；每一次发送，都是真心的表达。
          </Paragraph>

          <div style={{
            borderTop: '1px solid rgba(255,255,255,0.1)',
            paddingTop: '16px',
            fontSize: '11px',
            opacity: 0.6
          }}>
            © 2026 寄语信箱 - 测试版本 | 数据仅保存在本地浏览器
            <div style={{ marginTop: '4px' }}>
              Powered by OpenClaw & 小龙虾AI模型
            </div>
          </div>
        </div>
      </Footer>
      
      {/* 添加浮动动画样式 */}
      <style>
        {`
          @keyframes float {
            0%, 100% { transform: translateY(0); }
            50% { transform: translateY(-10px); }
          }
          
          @keyframes heartbeat {
            0%, 100% { transform: scale(1); }
            50% { transform: scale(1.1); }
          }
          
          .heart-beat {
            display: inline-block;
            animation: heartbeat 1.5s ease-in-out infinite;
          }
        `}
      </style>
    </Layout>
  );
};

export default JiyuMailboxPage;